import { SocialMediaTypes } from '../types/socialMedia';
import { SocialMedia } from '../api/user';

type NetworkConfig = {
  host: string;
  defaultPrefix: string;
  allowedPrefixes: string[];
  handlePattern: RegExp;
};

type ParsedHandle = { prefix: string; handle: string };

const NETWORK_CONFIG: Partial<Record<string, NetworkConfig>> = {
  [SocialMediaTypes.INSTAGRAM]: {
    host: 'instagram.com',
    defaultPrefix: '',
    allowedPrefixes: [''],
    handlePattern: /^[a-z0-9._]{1,30}$/,
  },
  [SocialMediaTypes.LINKEDIN]: {
    host: 'linkedin.com',
    defaultPrefix: 'in/',
    allowedPrefixes: ['in/', 'company/'],
    handlePattern: /^[a-z0-9-]{3,100}$/,
  },
  [SocialMediaTypes.FACEBOOK]: {
    host: 'facebook.com',
    defaultPrefix: '',
    allowedPrefixes: [''],
    handlePattern: /^[a-z0-9.-]{5,50}$/,
  },
};

const FACEBOOK_NUMERIC_ID = /profile\.php\?id=(\d+)/i;
const FACEBOOK_SHARE = /^share\/[^?#\s]+/i;

const isFacebookShare = (raw: string): boolean =>
  FACEBOOK_SHARE.test(
    raw
      .replace(/^https?:\/\//i, '')
      .replace(/^(www|m)\./i, '')
      .replace(/^facebook\.com\//i, ''),
  );

const parse = (config: NetworkConfig, raw: string): ParsedHandle | null => {
  let path = raw
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^(www|m)\./, '');

  if (path === config.host || path.startsWith(`${config.host}/`)) {
    path = path.slice(config.host.length).replace(/^\/+/, '');
  }

  path = path.split(/[?#]/)[0].replace(/\/+$/, '');

  const matchedPrefix = config.allowedPrefixes
    .filter((prefix) => prefix)
    .find((prefix) => path.startsWith(prefix));

  let prefix: string;
  let handle: string;
  if (matchedPrefix) {
    prefix = matchedPrefix;
    handle = path.slice(matchedPrefix.length);
  } else if (config.allowedPrefixes.includes('')) {
    prefix = '';
    handle = path;
  } else {
    if (path.includes('/')) {
      return null;
    }
    prefix = config.defaultPrefix;
    handle = path;
  }

  handle = handle.replace(/^@/, '');

  if (!handle || handle.includes('/') || !config.handlePattern.test(handle)) {
    return null;
  }

  return { prefix, handle };
};

const isValidWebsite = (value: string): boolean => {
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  try {
    return new URL(withScheme).hostname.includes('.');
  } catch {
    return false;
  }
};

export const isValidSocialLink = (network: string, value: string): boolean => {
  const raw = (value ?? '').trim();
  if (!raw) {
    return true;
  }

  if (network === SocialMediaTypes.WEBSITE) {
    return isValidWebsite(raw);
  }

  const config = NETWORK_CONFIG[network];
  if (!config) {
    return true;
  }

  if (
    network === SocialMediaTypes.FACEBOOK &&
    (FACEBOOK_NUMERIC_ID.test(raw) || isFacebookShare(raw))
  ) {
    return true;
  }

  return parse(config, raw) !== null;
};

export const checkWebsiteReachable = async (url: string): Promise<boolean> => {
  const target = /^https?:\/\//i.test(url) ? url : `https://${url}`;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    await fetch(target, {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
};

export const extractSocialUsername = (socialMedia: SocialMedia): string => {
  const raw = (socialMedia.link ?? '').trim();
  const config = NETWORK_CONFIG[socialMedia.network];

  if (!config) {
    return raw;
  }

  const parsed = parse(config, raw);
  return parsed ? parsed.handle : raw;
};
